/**
 * Standardized error types for the application
 */

export class AppError extends Error {
  constructor(
    message: string,
    public code?: string,
    public cause?: Error
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export class AuthError extends AppError {
  constructor(message: string, code?: string, cause?: Error) {
    super(message, code, cause);
    this.name = 'AuthError';
  }
}

export class NetworkError extends AppError {
  constructor(message: string, code?: string, cause?: Error) {
    super(message, code, cause);
    this.name = 'NetworkError';
  }
}

export class ValidationError extends AppError {
  constructor(
    message: string,
    public field?: string,
    cause?: Error
  ) {
    super(message, 'VALIDATION_ERROR', cause);
    this.name = 'ValidationError';
  }
}

export class SyncError extends AppError {
  constructor(message: string, code?: string, cause?: Error) {
    super(message, code, cause);
    this.name = 'SyncError';
  }
}

/**
 * Type guard to check if an error is an instance of AppError
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * Type guard to check if an error is a standard Error
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Safely extracts error message from unknown error type
 */
export function getErrorMessage(error: unknown): string {
  if (isError(error)) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unknown error occurred';
}

/**
 * Safely extracts error code from unknown error type
 */
export function getErrorCode(error: unknown): string | undefined {
  if (isAppError(error)) {
    return error.code;
  }
  if (isError(error) && 'code' in error) {
    return (error as Error & { code?: string }).code;
  }
  return undefined;
}

/**
 * Creates a standardized error from an unknown error type
 */
export function createStandardError(
  error: unknown,
  defaultMessage = 'An unexpected error occurred'
): AppError {
  if (isAppError(error)) {
    return error;
  }

  if (isError(error)) {
    return new AppError(error.message || defaultMessage, undefined, error);
  }

  if (typeof error === 'string') {
    return new AppError(error);
  }

  return new AppError(defaultMessage);
}

/**
 * Firebase error code mappings to user-friendly messages
 */
export const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  'auth/user-not-found': 'No account found with this email address',
  'auth/wrong-password': 'Incorrect password',
  'auth/email-already-in-use': 'An account with this email already exists',
  'auth/weak-password': 'Password is too weak',
  'auth/invalid-email': 'Invalid email address',
  'auth/too-many-requests': 'Too many login attempts. Please try again later',
  'auth/network-request-failed': 'Network error. Please check your connection',
  'permission-denied': 'You do not have permission to perform this action',
  unavailable: 'Service is temporarily unavailable. Please try again',
};

/**
 * Raised when an anonymous account cannot be upgraded because the email or
 * social identity already belongs to another account. The only recovery is a
 * plain sign-in (which changes uid), so callers need to tell this apart from a
 * generic failure to offer that choice.
 */
export const ACCOUNT_EXISTS = 'ACCOUNT_EXISTS';

/** Firebase codes that mean the identity is already attached to another user. */
const IDENTITY_TAKEN_CODES = [
  'auth/email-already-in-use',
  'auth/credential-already-in-use',
  'auth/account-exists-with-different-credential',
];

export function isIdentityTakenError(error: unknown): boolean {
  const code = getErrorCode(error);
  return !!code && IDENTITY_TAKEN_CODES.includes(code);
}

export function isAccountExistsError(error: unknown): boolean {
  return getErrorCode(error) === ACCOUNT_EXISTS;
}

/**
 * The credential linked but the follow-up sign-in did not. The account exists
 * and owns the original uid, yet the session still carries the anonymous
 * sign-in provider — so anything gated on that claim (public pack publishing)
 * would fail. Recovered by signing in, which keeps the uid.
 */
export const ACCOUNT_LINKED_NEEDS_SIGNIN = 'ACCOUNT_LINKED_NEEDS_SIGNIN';

/**
 * `provider-already-linked` says *this* user already holds the provider, so the
 * uid does not change and the "already taken" warning would be a lie — the fix
 * is the same sign-in that finishes any half-linked session.
 */
export function isAlreadyLinkedToThisUser(error: unknown): boolean {
  return getErrorCode(error) === 'auth/provider-already-linked';
}

export function isLinkedNeedsSignInError(error: unknown): boolean {
  return getErrorCode(error) === ACCOUNT_LINKED_NEEDS_SIGNIN;
}

/**
 * Converts Firebase error to user-friendly message
 */
export function getFirebaseErrorMessage(error: unknown): string {
  const code = getErrorCode(error);
  if (code && FIREBASE_ERROR_MESSAGES[code]) {
    return FIREBASE_ERROR_MESSAGES[code];
  }
  return getErrorMessage(error);
}
